import React, { useState } from 'react';
import HallOfFameTable from './HallOfFameTable';
import './styling.css';

export default function Best({ scripts, auth }) {
    const [showInfo, setShowInfo] = useState(false);
    const [filteredCount, setFilteredCount] = useState(scripts.length);

    // Matches the filter bar gradient exactly
    const seamlessBgStyle = {
        background: 'linear-gradient(135deg, #083A54 0%, #0A6A64 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
    };

    return (
        <>
            {/* CHANGED: container-xxl limits width on huge screens so it doesn't look stretched */}
            <div className="container-xxl">
                <div className="row justify-content-center pt-4">
                    <div className="col-lg-12">
                        
                        {/* UNIFIED HEADER BLOCK */}
                        {/* rounded-bottom-0 and mb-0 make it merge with the FilterBar below */}
                        <div className="shadow-sm rounded-top-2 text-white overflow-hidden mb-0" style={seamlessBgStyle}>
                            <div className="p-3">
                                {/* Added px-lg-2 to bring text in slightly from edges */}
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 px-lg-2">
                                    
                                    {/* Left: Title with Count Badge */}
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <h2 className="h4 mb-0 fw-bold">
                                            <img
                                                src="/images/icons/laurel_gold_light.png"
                                                alt=""
                                                width="37"
                                                height="20"
                                                className="me-2"
                                                style={{ verticalAlign: '-3px' }}
                                            />
                                            The Script Hall of Fame
                                        </h2>
                                        <span 
                                            className="badge"
                                            style={{
                                                background: filteredCount < scripts.length 
                                                    ? 'rgba(255, 193, 7, 0.3)' 
                                                    : 'rgba(255, 255, 255, 0.25)',
                                                color: 'white',
                                                padding: '6px 16px',
                                                borderRadius: '20px',
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                border: filteredCount < scripts.length
                                                    ? '1px solid rgba(255, 193, 7, 0.5)'
                                                    : '1px solid rgba(255, 255, 255, 0.4)'
                                            }}
                                        >
                                            {filteredCount === scripts.length 
                                                ? `${scripts.length} ${scripts.length === 1 ? 'Script' : 'Scripts'}`
                                                : `${filteredCount} of ${scripts.length} Scripts`
                                            }
                                        </span>
                                    </div>

                                    {/* Right: The Toggle - More visible */}
                                    <button 
                                        onClick={() => setShowInfo(!showInfo)}
                                        className="btn btn-sm"
                                        style={{ 
                                            fontSize: '0.85rem',
                                            background: 'rgba(255, 255, 255, 0.15)',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            color: 'white',
                                            fontWeight: 600,
                                            padding: '8px 16px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                        }}
                                    >
                                        <i className={`fa ${showInfo ? 'fa-chevron-up' : 'fa-info-circle'} me-2`}></i>
                                        {showInfo ? 'Close' : 'How Scoring Works'}
                                    </button>
                                </div>

                                {/* Updated description text - more contrast */}
                                <p 
                                    className="mb-0 small mt-2 px-lg-2" 
                                    style={{ 
                                        maxWidth: '800px', 
                                        lineHeight: '1.5',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    Discover exceptional scripts from our community. Contact writers directly to collaborate, hire, or simply share your praise.
                                </p>

                                {/* Collapsible Drawer: Math & Legend */}
                                {showInfo && (
                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-50 animate-fade-in px-lg-2">
                                        <div className="row g-4">
                                            {/* The Legend */}
                                            <div className="col-md-5 border-end border-secondary border-opacity-50">
                                                <h6 className="fw-bold text-uppercase small text-white-50 mb-2">Percentile Key</h6>
                                                <div className="d-flex flex-column gap-2 small">
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-success bg-gradient border border-success me-2" style={{width:'60px'}}>90%+</span>
                                                        <span><strong>Top Tier.</strong> Exceptional execution.</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-primary bg-gradient border border-primary me-2" style={{width:'60px'}}>70-89%</span>
                                                        <span><strong>Strong.</strong> Above average professional standard.</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-light text-dark border me-2" style={{width:'60px'}}>50-69%</span>
                                                        <span className="opacity-75"><strong>Solid.</strong> Competitive but needs polish.</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* The Math Explanation */}
                                            <div className="col-md-7">
                                                <h6 className="fw-bold text-uppercase small text-white-50 mb-2">How Scoring Works</h6>
                                                <div className="d-flex gap-3 small opacity-75">
                                                    <i className="fa fa-calculator fa-2x mt-1"></i>
                                                    <div>
                                                        <p className="mb-2">
                                                            Scores represent <strong>percentiles</strong> (0-100%), comparing each script against our Vault of professionally produced screenplays.
                                                        </p>
                                                        <p className="mb-0 fst-italic">
                                                            Example: A raw score of <strong>8.0</strong> might translate to the <strong>46th percentile</strong>. This means it scored better than 46% of pro scripts in that category.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Table Component (Contains the FilterBar) */}
                        {/* Added pb-5 for bottom spacing */}
                        <div className="pb-5 mb-4">
                            <HallOfFameTable scripts={scripts} auth={auth} onFilteredCountChange={setFilteredCount} />

                            {/* Awards legend — only shown when at least one script has awards */}
                            {scripts.some((s) => s.awards) && (
                                <p
                                    className="small mt-2 mb-0"
                                    style={{
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        background: 'rgba(8, 58, 84, 0.85)',
                                        display: 'inline-block',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                    }}
                                >
                                    <img
                                        src="/images/icons/laurel_gold_light.png"
                                        alt=""
                                        width="26"
                                        height="14"
                                        className="me-1"
                                        style={{ verticalAlign: '-2px' }}
                                    />
                                    Laurels mark awards &amp; contest placements, self-reported by the writer — hover over one to see them.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}